import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Grid, Card, Avatar, Chip, Divider } from '@mui/joy';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext.jsx';
import { useNavigate } from 'react-router-dom';
import EnhancedButton from '../../components/EnhancedButton';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const ClientGISProjects = () => {
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [gisProjects, setGisProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      setLoading(true);
      console.log('Authenticated user:', user);

      const { data: userClients, error: clientError } = await supabase
        .from('user_clients')
        .select('client_id')
        .eq('user_id', user.id);

      if (clientError) {
        console.error('Error fetching user clients:', clientError);
        setLoading(false);
        return;
      }
      console.log('User client links:', userClients);

      const clientIds = userClients.map(entry => entry.client_id);
      console.log('Client IDs:', clientIds);

      const { data: projData, error: projError } = await supabase
        .from('projects')
        .select('id, name')
        .in('client_id', clientIds);

      if (projError) {
        console.error('Error fetching projects:', projError);
        setLoading(false);
        return;
      }
      console.log('Projects:', projData);

      const projectIds = projData.map(p => p.id);
      console.log('Project IDs:', projectIds);

      const { data: gisData, error: gisError } = await supabase
        .from('gis_projects')
        .select('id, name, description, project_id, created_at')
        .in('project_id', projectIds);

      if (gisError) {
        console.error('Error fetching GIS projects:', gisError);
        setLoading(false);
        return;
      }
      console.log('GIS Projects:', gisData);

      setGisProjects(gisData || []);
      setProjects(projData || []);
      setLoading(false);
    };

    fetchData();
  }, [user]);

  // Helper functions
  const getProjectInitials = (projectName) => {
    return projectName?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'GS';
  };

  const getProjectColor = (index) => {
    const colors = ['#00A884', '#17a2b8', '#28a745', '#6c757d', '#ffc107', '#dc3545'];
    return colors[index % colors.length];
  };

  const grouped = gisProjects.reduce((acc, proj) => {
    const key = proj.project_id || 'no_project';
    if (!acc[key]) acc[key] = [];
    acc[key].push(proj);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ p: 0 }}>
        <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[...Array(3)].map((_, i) => (
            <Grid key={i} xs={12} sm={6} md={4}>
              <CardSkeleton height="200px" />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (gisProjects.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Avatar
          sx={{
            bgcolor: 'neutral.100',
            color: 'neutral.600',
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem'
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
            map
          </span>
        </Avatar>
        <Typography level="h5" sx={{ color: 'neutral.600', mb: 1 }}>No GIS Maps Found</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No GIS mapping projects found for your account. New maps will appear here when available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Summary Stats */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 'lg', background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.05) 0%, rgba(0, 126, 51, 0.02) 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            sx={{
              bgcolor: 'success.500',
              color: 'white',
              width: 56,
              height: 56
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
              satellite
            </span>
          </Avatar>
          <Box>
            <Typography level="h3" sx={{ color: 'success.700', fontWeight: 700 }}>
              {gisProjects.length} Interactive Map{gisProjects.length !== 1 ? 's' : ''}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Across {Object.keys(grouped).length} project{Object.keys(grouped).length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Projects and Maps */}
      {Object.entries(grouped).map(([projectId, maps]) => {
        const projectName = projectId === 'no_project'
          ? 'Unassigned GIS Projects'
          : projects.find(p => p.id === projectId)?.name || 'Unnamed Project';

        return (
          <Card key={projectId} sx={{ mb: 4, borderRadius: 'lg', overflow: 'hidden' }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'success.100', bgcolor: 'success.50' }}>
              <Typography 
                level="h4" 
                sx={{ 
                  color: 'success.700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontWeight: 600
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  folder
                </span>
                {projectName}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {maps.length} interactive map{maps.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {maps.map((gisProject, index) => (
                  <Grid xs={12} sm={6} md={4} key={gisProject.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                        }
                      }}
                      className="hover-lift"
                    >
                      {/* Map Header */}
                      <Box 
                        sx={{ 
                          p: 3, 
                          background: `linear-gradient(135deg, ${getProjectColor(index)}15 0%, ${getProjectColor(index)}05 100%)`,
                          borderBottom: '1px solid',
                          borderColor: 'neutral.200'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar 
                            sx={{ 
                              bgcolor: getProjectColor(index),
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.875rem'
                            }}
                          >
                            {getProjectInitials(gisProject.name)}
                          </Avatar>
                          
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              level="title-sm" 
                              sx={{ 
                                color: 'success.700',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {gisProject.name || 'Unnamed Map'}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                              Created: {new Date(gisProject.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Chip 
                          size="sm" 
                          variant="soft" 
                          sx={{
                            bgcolor: `${getProjectColor(index)}20`,
                            color: getProjectColor(index)
                          }}
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                              satellite
                            </span>
                          }
                        >
                          Interactive Map
                        </Chip>
                      </Box>

                      {/* Map Details */}
                      <Box sx={{ p: 3 }}>
                        <Typography 
                          level="body-sm" 
                          sx={{ 
                            mb: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '2.5em'
                          }}
                        >
                          {gisProject.description || 'Interactive mapping data for project visualization and analysis.'}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {/* Action Button */}
                        <EnhancedButton
                          gradient
                          fullWidth
                          onClick={() => navigate(`/client/gis-map/${gisProject.id}`)}
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                              map
                            </span>
                          }
                          sx={{
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(0, 168, 132, 0.25)',
                            }
                          }}
                        >
                          Open Interactive Map
                        </EnhancedButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export default ClientGISProjects;